import log from 'loglevel';
import { InferType, mixed, object, string } from 'yup';
import { removeEmptyValues } from '@move-frontend/utils';

import { Environment } from './types/Environment';

export const publicConfigSchema = object({
	version: string(),
	environment: mixed().oneOf(Object.values(Environment)).required(),
	logLevel: string().required(),
	baseUrl: string().required(),
	awsRegion: string(),
	algoliaAppId: string().required(),
	algoliaReadOnlyApiKey: string().required(),
	algoliaContentIndex: string().required(),
}).required();

export type PublicConfig = InferType<typeof publicConfigSchema>;

let config: PublicConfig | undefined;

export function getPublicConfig() {
	if (config) {
		return config;
	}

	const environment = (process.env.ENVIRONMENT ||
		process.env.NEXT_PUBLIC_ENVIRONMENT) as Environment;

	const defaults: Partial<PublicConfig> = {
		version: process.env.NEXT_PUBLIC_VERSION,
		environment,
		logLevel: 'warn',
		algoliaAppId: 'KOWFL7UXZN',
		algoliaReadOnlyApiKey: 'e318e9177da11bd185385a357129dacc',
		...getPublicConfigForEnvironment(environment),
	};

	const custom = getCustomPublicConfig();

	config = publicConfigSchema.validateSync({
		...defaults,
		...custom,
	});

	return config;
}

function getCustomPublicConfig() {
	const customConfig: Partial<PublicConfig> = {
		logLevel: process.env.NEXT_PUBLIC_LOG_LEVEL,
	};

	return removeEmptyValues(customConfig);
}

export function getPublicConfigForEnvironment(environment?: Environment) {
	if (!environment) {
		return {};
	}

	switch (environment) {
		case Environment.Development:
			return {
				logLevel: 'debug',
				baseUrl: 'http://dev.web.houstonairports.mobi',
				awsRegion: 'us-east-1',
				algoliaContentIndex: 'contentful_develop',
			};

		case Environment.Test:
			return {
				logLevel: 'info',
				baseUrl: 'https://test.web.houstonairports.mobi',
				awsRegion: 'us-east-1',
				algoliaContentIndex: 'contentful_test',
			};

		case Environment.Acceptance:
			return {
				logLevel: 'info',
				baseUrl: 'https://acc.web.houstonairports.mobi',
				awsRegion: 'us-east-1',
				algoliaContentIndex: 'contentful_acceptance',
			};

		case Environment.Production:
			return {
				baseUrl: 'https://fly2houston.com',
				awsRegion: 'us-east-1',
				algoliaContentIndex: 'contentful_master',
			};

		default:
			log.warn('No default public config for unknown environment', environment);
			return {};
	}
}
